/**
 * Horizontal drag containment for table column moves.
 *
 * Two cooperating pieces:
 * - `clampHorizontalToBounds` keeps the dragged overlay inside the visible
 *   viewport of the scroll container, so a column can never be pulled past
 *   the visible edge of the database block.
 * - `resolveHorizontalEdgeScrollIntent` turns the pointer position into a
 *   scroll intent when it enters an invisible zone along the left or right
 *   viewport edge (or leaves the viewport entirely). The DndContext drives
 *   the scroll container with that intent until the pointer moves back in.
 *
 * All values are viewport (client) coordinates.
 */
import type { DndClientRect, Modifier, Transform } from '../types.js';

export type HorizontalBounds = Pick<DndClientRect, 'left' | 'right'>;

export type HorizontalEdgeScrollIntent = {
  /** -1 scrolls towards the start, 1 towards the end, 0 idle. */
  direction: -1 | 0 | 1;
  /** 0..1 depth into the edge zone; 1 once the pointer leaves the viewport. */
  intensity: number;
};

export const IDLE_HORIZONTAL_EDGE_SCROLL_INTENT: HorizontalEdgeScrollIntent = {
  direction: 0,
  intensity: 0,
};

/** Width of the invisible scroll-intent zone at each viewport edge. */
export const DEFAULT_HORIZONTAL_EDGE_ZONE_WIDTH = 48;

/** Scroll distance per second at full intensity. */
export const DEFAULT_HORIZONTAL_EDGE_SCROLL_SPEED = 900;

const clampUnit = (value: number): number => Math.min(1, Math.max(0, value));

export function clampHorizontalTransform(args: {
  transform: Transform;
  activeNodeRect: Pick<DndClientRect, 'left' | 'width'>;
  overlayNodeRect?: Pick<DndClientRect, 'width'>;
  bounds: HorizontalBounds;
}): Transform {
  const { transform, activeNodeRect, overlayNodeRect, bounds } = args;
  const width = overlayNodeRect?.width ?? activeNodeRect.width;
  const minX = bounds.left - activeNodeRect.left;
  const maxX = Math.max(minX, bounds.right - width - activeNodeRect.left);
  const x = Math.min(Math.max(transform.x, minX), maxX);
  return x === transform.x ? transform : { ...transform, x };
}

export function resolveHorizontalEdgeScrollIntent(args: {
  pointerX: number;
  bounds: HorizontalBounds;
  zoneWidth: number;
}): HorizontalEdgeScrollIntent {
  const { pointerX, bounds, zoneWidth } = args;
  const viewportWidth = bounds.right - bounds.left;
  if (zoneWidth <= 0 || viewportWidth <= 0) {
    return IDLE_HORIZONTAL_EDGE_SCROLL_INTENT;
  }
  const zone = Math.min(zoneWidth, viewportWidth / 2);
  const leftEdge = bounds.left + zone;
  const rightEdge = bounds.right - zone;
  if (pointerX < leftEdge) {
    return {
      direction: -1,
      intensity: clampUnit((leftEdge - pointerX) / zone),
    };
  }
  if (pointerX > rightEdge) {
    return {
      direction: 1,
      intensity: clampUnit((pointerX - rightEdge) / zone),
    };
  }
  return IDLE_HORIZONTAL_EDGE_SCROLL_INTENT;
}

export function horizontalEdgeScrollStep(
  intent: HorizontalEdgeScrollIntent,
  elapsedMs: number,
  speed: number = DEFAULT_HORIZONTAL_EDGE_SCROLL_SPEED
): number {
  if (intent.direction === 0 || elapsedMs <= 0 || speed <= 0) {
    return 0;
  }
  return intent.direction * intent.intensity * speed * (elapsedMs / 1000);
}

/**
 * Modifier factory: keeps the dragged overlay inside `getBounds()`.
 * Returns the transform untouched when no bounds are available.
 */
export function clampHorizontalToBounds(
  getBounds: () => HorizontalBounds | undefined
): Modifier {
  return ({ transform, activeNodeRect, overlayNodeRect }) => {
    const bounds = getBounds();
    if (!bounds) {
      return transform;
    }
    return clampHorizontalTransform({
      transform,
      activeNodeRect,
      overlayNodeRect,
      bounds,
    });
  };
}
